// src/analytics/events.ts
var AnalyticsEvents = {
  // Navigation
  PAGE_VIEW: "$pageview",
  // Using PostHog's standard pageview event
  // Authentication
  SIGN_IN_STARTED: "sign_in_started",
  SIGN_IN_OTP_SENT: "sign_in_otp_sent",
  SIGN_IN_COMPLETED: "sign_in_completed",
  SIGN_IN_FAILED: "sign_in_failed",
  SIGN_OUT: "sign_out",
  ACCOUNT_CREATED: "account_created",
  // Product Browsing
  BRAND_VIEWED: "brand_viewed",
  PRODUCT_LIST_VIEWED: "product_list_viewed",
  PRODUCT_VIEWED: "product_viewed",
  PRODUCT_SEARCH: "product_search",
  PRODUCT_FILTER_APPLIED: "product_filter_applied",
  // Cart
  CART_VIEWED: "cart_viewed",
  PRODUCT_ADDED_TO_CART: "product_added_to_cart",
  PRODUCT_REMOVED_FROM_CART: "product_removed_from_cart",
  CART_QUANTITY_UPDATED: "cart_quantity_updated",
  CART_CLEARED: "cart_cleared",
  // Bank Account
  BANK_ACCOUNT_ADD_FIRST_CLICKED: "bank_account_add_first_clicked",
  BANK_ACCOUNT_ADD_ANOTHER_CLICKED: "bank_account_add_another_clicked",
  BANK_ACCOUNT_FORM_SUBMITTED: "bank_account_form_submitted",
  // Checkout
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_ADDRESS_SELECTED: "checkout_address_selected",
  CHECKOUT_PAYMENT_SELECTED: "checkout_payment_selected",
  CHECKOUT_DISCOUNT_APPLIED: "checkout_discount_applied",
  CHECKOUT_COMPLETED: "checkout_completed",
  CHECKOUT_FAILED: "checkout_failed",
  // Order
  ORDER_CONFIRMED: "order_confirmed",
  // Promotions
  PROMOTION_VIEWED: "promotion_viewed",
  PROMOTION_CLICKED: "promotion_clicked",
  PROMOTION_DISMISSED: "promotion_dismissed",
  // Widget-specific (embed-frontend)
  WIDGET_INITIALIZED: "widget_initialized",
  WIDGET_CONFIG_LOADED: "widget_config_loaded"
};

// src/analytics/analytics-context.tsx
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from "react";

// src/analytics/posthog-adapter.ts
import posthog from "posthog-js";
var PostHogAdapter = class {
  initialized = false;
  disabled = false;
  config = null;
  initialize(config) {
    console.info("PostHog initialization started", {
      apiKey: config.apiKey ? `${config.apiKey.slice(0, 10)}...` : "MISSING",
      apiHost: config.apiHost,
      appSource: config.appSource,
      hostDomain: config.hostDomain,
      disabled: config.disabled,
      autocapture: config.autocapture,
      capturePageview: config.capturePageview
    });
    if (this.initialized) {
      console.debug("PostHog already initialized, skipping");
      return;
    }
    if (config.disabled) {
      console.warn("PostHog disabled via config.disabled flag");
      this.disabled = true;
      return;
    }
    if (!config.apiKey) {
      console.warn("PostHog disabled - no API key provided (VITE_POSTHOG_KEY)");
      this.disabled = true;
      return;
    }
    this.config = config;
    posthog.init(config.apiKey, {
      api_host: config.apiHost ?? "https://us.i.posthog.com",
      autocapture: config.autocapture ?? false,
      capture_pageview: config.capturePageview ?? false,
      persistence: "localStorage",
      // Session replay: enabled for storefront, disabled for embed-frontend (Shadow DOM compatibility)
      disable_session_recording: !(config.sessionReplay ?? false),
      // Heatmaps disabled for Shadow DOM compatibility in embed-frontend
      capture_heatmaps: config.sessionReplay ?? false,
      session_recording: {
        maskAllInputs: false,
        maskInputFn: (text, element) => {
          if (element?.id === "account-number" || element?.id === "account-number-confirm") {
            return "*".repeat(text.length);
          }
          return text;
        }
      }
    });
    posthog.register({
      app_source: config.appSource,
      ...config.hostDomain && { host_domain: config.hostDomain }
    });
    if (config.debug) {
      posthog.debug(true);
    }
    this.initialized = true;
    console.info("PostHog initialized successfully", { appSource: config.appSource });
  }
  identify(user) {
    if (this.disabled || !this.initialized) {
      console.debug("PostHog identify skipped (disabled or not initialized)");
      return;
    }
    console.info("PostHog identify", { userId: user.id, email: user.email });
    posthog.identify(user.id, {
      email: user.email,
      company_id: user.companyId,
      company_name: user.companyName,
      customer_id: user.customerId
    });
  }
  reset() {
    if (this.disabled || !this.initialized) return;
    console.info("PostHog reset");
    posthog.reset();
  }
  track(event, properties) {
    if (this.disabled || !this.initialized) {
      console.debug("PostHog track skipped (disabled or not initialized)", { event });
      return;
    }
    console.debug("PostHog track", { event, properties });
    posthog.capture(event, properties);
  }
  group(group) {
    if (this.disabled || !this.initialized) return;
    posthog.group(group.type, group.id, group.properties);
  }
  isFeatureEnabled(flag) {
    if (this.disabled || !this.initialized) return false;
    return posthog.isFeatureEnabled(flag) ?? false;
  }
  getFeatureFlag(flag) {
    if (this.disabled || !this.initialized) return void 0;
    return posthog.getFeatureFlag(flag);
  }
  setPersonProperties(properties) {
    if (this.disabled || !this.initialized) return;
    posthog.setPersonProperties(properties);
  }
  optIn() {
    if (!this.initialized) return;
    posthog.opt_in_capturing();
    this.disabled = false;
  }
  optOut() {
    if (!this.initialized) return;
    posthog.opt_out_capturing();
    this.disabled = true;
  }
  getConfig() {
    return this.config;
  }
  isDisabled() {
    return this.disabled;
  }
};
var postHogAdapter = new PostHogAdapter();

// src/analytics/analytics-context.tsx
import { jsx } from "react/jsx-runtime";
var AnalyticsContext = createContext(null);
function AnalyticsProvider({
  children,
  config
}) {
  const adapterRef = useRef(null);
  const initializedRef = useRef(false);
  if (!adapterRef.current) {
    adapterRef.current = new PostHogAdapter();
  }
  const adapter = adapterRef.current;
  useEffect(() => {
    if (!initializedRef.current && config.apiKey) {
      adapter.initialize(config);
      initializedRef.current = true;
    }
  }, [adapter, config]);
  const track = useCallback(
    (event, properties) => {
      adapter.track(event, properties);
    },
    [adapter]
  );
  const identify = useCallback(
    (user) => {
      adapter.identify(user);
    },
    [adapter]
  );
  const reset = useCallback(() => {
    adapter.reset();
  }, [adapter]);
  const group = useCallback(
    (grp) => {
      adapter.group(grp);
    },
    [adapter]
  );
  const isFeatureEnabled = useCallback(
    (flag) => {
      return adapter.isFeatureEnabled(flag);
    },
    [adapter]
  );
  const getFeatureFlag = useCallback(
    (flag) => {
      return adapter.getFeatureFlag(flag);
    },
    [adapter]
  );
  const setPersonProperties = useCallback(
    (properties) => {
      adapter.setPersonProperties(properties);
    },
    [adapter]
  );
  const contextValue = useMemo(
    () => ({
      track,
      identify,
      reset,
      group,
      isFeatureEnabled,
      getFeatureFlag,
      setPersonProperties,
      isInitialized: initializedRef.current,
      config
    }),
    [
      track,
      identify,
      reset,
      group,
      isFeatureEnabled,
      getFeatureFlag,
      setPersonProperties,
      config
    ]
  );
  return /* @__PURE__ */ jsx(AnalyticsContext.Provider, { value: contextValue, children });
}
function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error(
      "useAnalyticsContext must be used within an AnalyticsProvider"
    );
  }
  return context;
}
function useAnalyticsSafe() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    return {
      track: () => {
      },
      identify: () => {
      },
      reset: () => {
      },
      group: () => {
      },
      isFeatureEnabled: () => false,
      getFeatureFlag: () => void 0,
      setPersonProperties: () => {
      },
      isInitialized: false,
      config: null
    };
  }
  return context;
}

// src/analytics/hooks.ts
import { useCallback as useCallback2, useMemo as useMemo2 } from "react";
function useAnalytics() {
  return useAnalyticsSafe();
}
function useFeatureFlag(flag) {
  const { isFeatureEnabled } = useAnalyticsSafe();
  return isFeatureEnabled(flag);
}
function useFeatureFlagVariant(flag) {
  const { getFeatureFlag } = useAnalyticsSafe();
  return getFeatureFlag(flag);
}
function useFunnelTracking() {
  const { track, identify } = useAnalyticsSafe();
  return useMemo2(
    () => ({
      // Auth events
      trackSignInStarted: (props) => track(AnalyticsEvents.SIGN_IN_STARTED, props),
      trackSignInOtpSent: (props) => track(AnalyticsEvents.SIGN_IN_OTP_SENT, props),
      trackSignInCompleted: (props) => {
        identify({
          id: props.userId,
          email: props.email,
          companyId: props.companyId,
          companyName: props.companyName
        });
        track(AnalyticsEvents.SIGN_IN_COMPLETED, props);
      },
      trackSignInFailed: (props) => track(AnalyticsEvents.SIGN_IN_FAILED, props),
      trackSignOut: () => track(AnalyticsEvents.SIGN_OUT, {}),
      trackAccountCreated: (props) => track(AnalyticsEvents.ACCOUNT_CREATED, props),
      // Product events
      trackBrandViewed: (props) => track(AnalyticsEvents.BRAND_VIEWED, props),
      trackProductListViewed: (props) => track(AnalyticsEvents.PRODUCT_LIST_VIEWED, props),
      trackProductViewed: (props) => track(AnalyticsEvents.PRODUCT_VIEWED, props),
      trackProductSearch: (query) => track(AnalyticsEvents.PRODUCT_SEARCH, { query }),
      trackProductFilterApplied: (props) => track(AnalyticsEvents.PRODUCT_FILTER_APPLIED, props),
      // Cart events
      trackCartViewed: (props) => track(AnalyticsEvents.CART_VIEWED, props),
      trackProductAddedToCart: (props) => track(AnalyticsEvents.PRODUCT_ADDED_TO_CART, props),
      trackProductRemovedFromCart: (props) => track(AnalyticsEvents.PRODUCT_REMOVED_FROM_CART, props),
      trackCartQuantityUpdated: (props) => track(AnalyticsEvents.CART_QUANTITY_UPDATED, props),
      trackCartCleared: () => track(AnalyticsEvents.CART_CLEARED, {}),
      // Bank Account events
      trackBankAccountAddFirstClicked: (props) => track(AnalyticsEvents.BANK_ACCOUNT_ADD_FIRST_CLICKED, props),
      trackBankAccountAddAnotherClicked: (props) => track(AnalyticsEvents.BANK_ACCOUNT_ADD_ANOTHER_CLICKED, props),
      trackBankAccountFormSubmitted: (props) => track(AnalyticsEvents.BANK_ACCOUNT_FORM_SUBMITTED, props),
      // Checkout events
      trackCheckoutStarted: (props) => track(AnalyticsEvents.CHECKOUT_STARTED, props),
      trackCheckoutAddressSelected: (props) => track(AnalyticsEvents.CHECKOUT_ADDRESS_SELECTED, props),
      trackCheckoutPaymentSelected: (props) => track(AnalyticsEvents.CHECKOUT_PAYMENT_SELECTED, props),
      trackCheckoutDiscountApplied: (props) => track(AnalyticsEvents.CHECKOUT_DISCOUNT_APPLIED, props),
      trackCheckoutCompleted: (props) => track(AnalyticsEvents.CHECKOUT_COMPLETED, props),
      trackCheckoutFailed: (reason) => track(AnalyticsEvents.CHECKOUT_FAILED, { reason }),
      // Order events
      trackOrderConfirmed: (props) => track(AnalyticsEvents.ORDER_CONFIRMED, props),
      // Promotion events
      trackPromotionViewed: (props) => track(AnalyticsEvents.PROMOTION_VIEWED, props),
      trackPromotionClicked: (props) => track(AnalyticsEvents.PROMOTION_CLICKED, props),
      trackPromotionDismissed: (props) => track(AnalyticsEvents.PROMOTION_DISMISSED, props)
    }),
    [track, identify]
  );
}
function useGroupAnalytics() {
  const { group } = useAnalyticsSafe();
  const setCompanyGroup = useCallback2(
    (companyId, properties) => {
      group({
        type: "company",
        id: companyId,
        properties
      });
    },
    [group]
  );
  const setBrandGroup = useCallback2(
    (brandId, properties) => {
      group({
        type: "brand",
        id: brandId,
        properties
      });
    },
    [group]
  );
  return {
    setCompanyGroup,
    setBrandGroup
  };
}

// src/analytics/RouteTracker.tsx
import { useEffect as useEffect2, useRef as useRef2 } from "react";
import { useLocation } from "react-router-dom";
var ROUTE_NAMES = {
  // Storefront
  "/": "Home",
  "/brands": "Brands",
  "/products": "All Products",
  "/checkout": "Checkout",
  "/order-confirmation": "Order Confirmation",
  "/terms": "Terms",
  "/error": "Error",
  // Supplier-portal
  "/dashboard": "Portal \u2014 Dashboard",
  "/inventory": "Portal \u2014 Inventory",
  "/orders": "Portal \u2014 Orders",
  "/transfers": "Portal \u2014 Transfers",
  "/deliveries": "Portal \u2014 Deliveries",
  "/drivers": "Portal \u2014 Drivers",
  "/analytics": "Portal \u2014 Analytics",
  "/settings": "Portal \u2014 Settings",
  "/admin": "Portal \u2014 Admin",
  "/login": "Portal \u2014 Login",
  "/discount-codes": "Portal \u2014 Discount Codes"
};
function getPageName(pathname) {
  if (ROUTE_NAMES[pathname]) {
    return ROUTE_NAMES[pathname];
  }
  if (pathname.startsWith("/products/")) {
    return "Products";
  }
  if (pathname.startsWith("/brands/") && pathname.includes("/products")) {
    return "Brand Products";
  }
  if (pathname.startsWith("/brands/")) {
    return "Brand Detail";
  }
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];
  if (firstSegment) {
    return firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
  }
  return "Unknown";
}
function RouteTracker({ hostDomain, basePath = "" }) {
  const location = useLocation();
  const { track } = useAnalyticsSafe();
  const previousPathRef = useRef2(null);
  const isInitialMount = useRef2(true);
  useEffect2(() => {
    const currentPath = location.pathname;
    if (previousPathRef.current === currentPath && !isInitialMount.current) {
      return;
    }
    const referrer = previousPathRef.current ? `${basePath}${previousPathRef.current}` : document.referrer;
    const virtualUrl = hostDomain ? `https://${hostDomain}${basePath}${currentPath}` : `${window.location.origin}${basePath}${currentPath}`;
    const pageName = getPageName(currentPath);
    track(AnalyticsEvents.PAGE_VIEW, {
      $current_url: virtualUrl,
      $pathname: `${basePath}${currentPath}`,
      $referrer: referrer,
      page_name: pageName,
      route: currentPath
    });
    previousPathRef.current = currentPath;
    isInitialMount.current = false;
  }, [location.pathname, track, hostDomain, basePath]);
  return null;
}

export {
  AnalyticsEvents,
  PostHogAdapter,
  postHogAdapter,
  AnalyticsProvider,
  useAnalyticsContext,
  useAnalyticsSafe,
  useAnalytics,
  useFeatureFlag,
  useFeatureFlagVariant,
  useFunnelTracking,
  useGroupAnalytics,
  RouteTracker
};
